import React, { useEffect, useState } from 'react';
import '../../styles/jobportal.css';
import ApplicationCard from './components/ApplicationCard';
import JobCard from './components/JobCard';
import DrivesPopup from './components/DrivesPopup';
import StudentPlacementOverview from './components/StudentPlacementOverview';
import StudentProfileOverview from './components/StudentProfileOverview';
import JobDetailsPopup from './components/JobDetailsPopup';
import ApplyPopup from './components/ApplyPopup';
import { useSelector } from 'react-redux';
import {
	fetchApplicationCards,
	fetchJobCards,
	fetchJobPortalDashboard,
	fetchMockScoreCards,
	fetchMockSessionDetails,
	fetchOffCampusCards,
} from '../../redux/jobportal/jobPortalSlice';
import { RootState, useAppDispatch } from '../../redux/store';
import Loader from '../../components/Loader';
import CompleteProfilePopup from './components/CompleteProfilePopup';
import { services } from '../../apis';
import MockScorePopup from './components/MockScorePopup';
import OffCampusCard from './components/OffCampusCard';

export interface PopupState {
	isOpen: boolean;
	identifier: string;
}

const JobPortal = () => {
	// tab state
	const [activeTab, setActiveTab] = useState(4);

	// popup states
	const [profilePopup, setProfilePopup] = useState<PopupState>({
		isOpen: false,
		identifier: '',
	});
	const [drivesPopup, setDrivesPopup] = useState<PopupState>({
		isOpen: false,
		identifier: '',
	});
	const [mockScorePopup, setMockScorePopup] = useState<PopupState>({
		isOpen: false,
		identifier: '',
	});
	const [jobDetailsPopup, setJobDetailsPopup] = useState<PopupState>({
		isOpen: false,
		identifier: '',
	});
	const [applyPopup, setApplyPopup] = useState<PopupState>({
		isOpen: false,
		identifier: '',
	});

	// get global state
	const jobPortalState = useSelector((state: RootState) => state.jobPortal);
	const {
		jobCardsState,
		applicationCardsState,
		offCampusCardsState,
		mockScoreCardsState,
		jobPortalDashboardState,
		jobPortalMockState,
	} = jobPortalState;

	// dispatch
	const dispatch = useAppDispatch();

	// 0. check if profile completed fetch profile overview, fetch dashboard overview
	useEffect(() => {
		const isProfileCompleted = async () => {
			const data = await services.jobPortalService.isProfileCompleted();
			if (data !== true) {
				setProfilePopup({
					isOpen: true,
					identifier: '',
				});
			}
		};

		isProfileCompleted();

		if (jobPortalMockState.jobPortalMock === null)
			dispatch(fetchMockSessionDetails());

		if (jobPortalDashboardState.jobPortalDashboard === null)
			dispatch(fetchJobPortalDashboard());
	}, []);

	// 1. fetch job cards
	useEffect(() => {
		if (activeTab === 1 && jobCardsState.jobCards.length === 0) {
			dispatch(fetchJobCards());
		}
	}, [activeTab]);

	// 2. fetch application cards
	useEffect(() => {
		if (
			activeTab === 2 &&
			applicationCardsState.applicationCards.length === 0
		) {
			dispatch(fetchApplicationCards());
		}
	}, [activeTab]);

	// 2. fetch off campus cards
	useEffect(() => {
		if (
			activeTab === 3 &&
			offCampusCardsState.offCampusCards.length === 0
		) {
			dispatch(fetchOffCampusCards());
		}
	}, [activeTab]);

	useEffect(() => {
		if (
			activeTab === 4 &&
			mockScoreCardsState.mockScoreCards.length === 0
		) {
			dispatch(fetchMockScoreCards());
		}
	}, [activeTab]);

	return (
		<div className="job-portal">
			<div className="column-1">
				<div className="tab-container">
					<div className="tabs">
						<button
							className={activeTab === 4 ? 'active' : ''}
							onClick={() => {
								setActiveTab(4);
							}}
						>
							Jobs For You
						</button>
						<button
							className={activeTab === 1 ? 'active' : ''}
							onClick={() => {
								setActiveTab(1);
							}}
						>
							Explore Job Drives
						</button>
						<button
							className={activeTab === 2 ? 'active' : ''}
							onClick={() => {
								setActiveTab(2);
							}}
						>
							Your Applications
						</button>
						<button
							className={activeTab === 3 ? 'active' : ''}
							onClick={() => {
								setActiveTab(3);
							}}
						>
							Off Campus
						</button>
					</div>
					<div className="tab-content">
						{/* Active Tab === 1 START*/}
						{activeTab === 1 && jobCardsState.loading === true && (
							<div
								style={{
									width: '200px',
									marginTop: '2rem',
									transform: 'scale(0.8)',
								}}
							>
								<Loader />
							</div>
						)}
						{activeTab === 1 &&
							jobCardsState.loading === false &&
							jobCardsState.jobCards.length !== 0 && (
								<>
									{jobCardsState.jobCards.map((card, idx) => (
										<JobCard
											key={idx}
											data={card}
											setJobDetailsPopup={
												setJobDetailsPopup
											}
											setApplyPopup={setApplyPopup}
										/>
									))}
								</>
							)}
						{activeTab === 1 &&
							jobCardsState.loading === false &&
							jobCardsState.jobCards.length === 0 && (
								<p>
									No new jobs are available, please try again
									later
								</p>
							)}
						{/* Active Tab === 1 END */}

						{/* Active Tab === 2 START */}
						{activeTab === 2 &&
							applicationCardsState.loading === true && (
								<div
									style={{
										width: '200px',
										marginTop: '2rem',
										transform: 'scale(0.8)',
									}}
								>
									<Loader />
								</div>
							)}
						{activeTab === 2 &&
							applicationCardsState.loading === false &&
							applicationCardsState.applicationCards.length !==
								0 && (
								<>
									{applicationCardsState.applicationCards.map(
										(card, idx) => (
											<ApplicationCard
												data={card}
												key={idx}
												setJobDetailsPopup={
													setJobDetailsPopup
												}
											/>
										)
									)}
								</>
							)}
						{activeTab === 2 &&
							applicationCardsState.loading === false &&
							applicationCardsState.applicationCards.length ===
								0 && <p>You have no existing applications</p>}
						{/* Active Tab === 2 END */}

						{/* Active Tab === 3 START */}
						{activeTab === 3 &&
							offCampusCardsState.loading === true && (
								<div
									style={{
										width: '200px',
										marginTop: '2rem',
										transform: 'scale(0.8)',
									}}
								>
									<Loader />
								</div>
							)}
						{activeTab === 3 &&
							offCampusCardsState.loading === false &&
							offCampusCardsState.offCampusCards.length !== 0 && (
								<>
									{offCampusCardsState.offCampusCards.map(
										(card, idx) => (
											<OffCampusCard
												data={card}
												key={idx}
												setJobDetailsPopup={
													setJobDetailsPopup
												}
											/>
										)
									)}
								</>
							)}
						{activeTab === 3 &&
							offCampusCardsState.loading === false &&
							offCampusCardsState.offCampusCards.length === 0 && (
								<p>You have no existing applications</p>
							)}
						{/* Active Tab === 3 END */}

						{/* Active Tab === 4 START */}
						{activeTab === 4 &&
							mockScoreCardsState.loading === true && (
								<div
									style={{
										width: '200px',
										marginTop: '2rem',
										transform: 'scale(0.8)',
									}}
								>
									<Loader />
								</div>
							)}
						{activeTab === 4 &&
							mockScoreCardsState.loading === false &&
							mockScoreCardsState.mockScoreCards.length !== 0 && (
								<>
									{mockScoreCardsState.mockScoreCards.map(
										(card, idx) => (
											<JobCard
												key={idx}
												data={card}
												setJobDetailsPopup={
													setJobDetailsPopup
												}
												setApplyPopup={setApplyPopup}
											/>
										)
									)}
								</>
							)}
						{activeTab === 4 &&
							mockScoreCardsState.loading === false &&
							mockScoreCardsState.mockScoreCards.length === 0 && (
								<p>
									No new jobs are available, please try again
									later
								</p>
							)}
						{/* Active Tab === 4 END */}
					</div>
				</div>
			</div>
			<div className="column-2">
				<StudentProfileOverview setMockScorePopup={setMockScorePopup} />
				<StudentPlacementOverview setDrivesPopup={setDrivesPopup} />
			</div>
			{/* Popups START */}
			{/* <CompleteProfilePopup
				profilePopup={profilePopup}
				setProfilePopup={setProfilePopup}
			/> */}
			<DrivesPopup
				drivesPopup={drivesPopup}
				setDrivesPopup={setDrivesPopup}
			/>
			<MockScorePopup
				mockScorePopup={mockScorePopup}
				setMockScorePopup={setMockScorePopup}
			/>
			<JobDetailsPopup
				jobDetailsPopup={jobDetailsPopup}
				setJobDetailsPopup={setJobDetailsPopup}
				setApplyPopup={setApplyPopup}
			/>
			<ApplyPopup
				applyPopup={applyPopup}
				setApplyPopup={setApplyPopup}
			/>
			{/* Popups END */}
		</div>
	);
};

export default JobPortal;
